import { AfterViewInit, Component, inject, NgZone, OnDestroy, OnInit, signal } from '@angular/core';
import { IonicModule, ModalController, NavController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { NavigationEnd, Router } from '@angular/router';
import { environment } from '@fitup-monorepo/core/lib/environment';
import { filter, takeUntil } from 'rxjs/operators';
import { firstValueFrom, fromEvent, Subject, Subscription } from 'rxjs';
import moment from 'moment';
import { register } from 'swiper/element/bundle';
import { Store } from '@ngrx/store';
import { BrowserPlugin } from '@capacitor/browser';
import { App, AppPlugin, URLOpenListenerEvent } from '@capacitor/app';
import { APP, BROWSER, WINDOW } from '@fitup-monorepo/core/lib/capacitor-injection-tokens';
import { AppScreenOrientationService } from '@fitup-monorepo/core/lib/services/app-screen-orientation/app-screen-orientation.service';
import { LoggingService } from '@fitup-monorepo/core/lib/services/logging/logging.service';
import { StateService } from '@fitup-monorepo/core/lib/services/state/state.service';
import { PlatformService } from '@fitup-monorepo/core/lib/services/platform/platform.service';
import { CustomAppService } from '@fitup-monorepo/core/lib/services/custom-app/custom-app.service';
import { AuthServerProvider } from '@fitup-monorepo/core/lib/services/auth/auth-jwt.service';
import { profileApiActions } from '@fitup-monorepo/core/lib/state/profile/profile.actions';
import { AppVersionInterceptor } from '@fitup-monorepo/core/lib/interceptors/app-version.interceptor';
import { selectCustomer } from '@fitup-monorepo/core/lib/state/profile/profile.selector';
import { ActivityStorageService } from '@fitup-monorepo/activity/lib/services/activity-storage.service';
import { LocationService } from '@fitup-monorepo/activity/lib/services/location.service';
import { HeartRateService } from '@fitup-monorepo/core/lib/services/heart-rate/heart-rate.service';
import { SplashScreen } from '@capacitor/splash-screen';
import { Location } from '@angular/common';
import { LocalStorageService } from '@fitup-monorepo/core/lib/services/local-storage.service';
import { LiveUpdateService } from '@fitup-monorepo/core/lib/services/live-update/live-update.service';
import { BackgroundFetch } from '@transistorsoft/capacitor-background-fetch';
import { HealthStatsService } from '@fitup-monorepo/home-dashboard/lib/components/health-stats/health-stats.service';
import { AudioService } from '@fitup-monorepo/core/lib/services/audio/audio.service';
import { Permission, PermissionsService } from '@fitup-monorepo/core/lib/services/permissions/permissions.service';
import { StatusBarService } from '@fitup-monorepo/core/lib/services/status-bar/status-bar.service';
import { AppRoutes } from '@fitup-monorepo/routes/lib/app-routes';
import { redactParams, redactUrl } from '@fitup-monorepo/core/lib/util/redact.util';
import { EnvService } from '@fitup-monorepo/core/lib/env.service';

register();

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [IonicModule],
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly browser: BrowserPlugin = inject(BROWSER);
  private readonly app: AppPlugin = inject(APP);
  private readonly window: Window = inject(WINDOW);
  private translate: TranslateService = inject(TranslateService);
  private localStorageService: LocalStorageService = inject(LocalStorageService);
  public navController: NavController = inject(NavController);
  private platform: Platform = inject(Platform);
  public screenOrientation: AppScreenOrientationService = inject(AppScreenOrientationService);
  private router: Router = inject(Router);
  private ngZone: NgZone = inject(NgZone);
  public translateService: TranslateService = inject(TranslateService);
  private activityStorageService: ActivityStorageService = inject(ActivityStorageService);
  private locationService: LocationService = inject(LocationService);
  private heartRateService: HeartRateService = inject(HeartRateService);
  private healthStatsService: HealthStatsService = inject(HealthStatsService);
  private readonly loggingService: LoggingService = inject(LoggingService);
  private readonly stateService: StateService = inject(StateService);
  private readonly platformService: PlatformService = inject(PlatformService);
  private readonly customerAppService: CustomAppService = inject(CustomAppService);
  private readonly modalController: ModalController = inject(ModalController);
  private readonly authServerProvider: AuthServerProvider = inject(AuthServerProvider);
  private readonly location: Location = inject(Location);
  private readonly liveUpdateService: LiveUpdateService = inject(LiveUpdateService);
  private readonly audioService: AudioService = inject(AudioService);
  private readonly statusBarService: StatusBarService = inject(StatusBarService);
  private readonly envService: EnvService = inject(EnvService);
  private store: Store = inject(Store);

  public closed$ = new Subject<void>();
  private backButtonSubscription: Subscription;

  protected readonly customClass = signal<string>('');

  public async ngAfterViewInit(): Promise<void> {
    await this.screenOrientation.init('AppComponent.afterViewInit');

    await this.registerBackgroundTask();
  }

  public async ngOnInit(): Promise<void> {
    await this.initializeApp();
    this.setCustomClass();

    this.platform.pause.pipe(takeUntil(this.closed$)).subscribe(() => {
      console.log('App went into background', JSON.stringify(this.stateService.getState()));
    });

    this.platform.resume.pipe(takeUntil(this.closed$)).subscribe(() => {
      console.log('App resumed!', JSON.stringify(this.stateService.getState()));

      App.getLaunchUrl().then(data => {
        console.log('App.getLaunchUrl', JSON.stringify(data));
      });

      this.authServerProvider.updateJWTIfNecessary();
    });

    await this.authServerProvider.updateJWTIfNecessary();

    window.addEventListener('online', () => {
      console.log('App went back online');
      this.authServerProvider.isAuthenticated().then(isAuthenticated => {
        if (isAuthenticated) {
          this.activityStorageService.checkForUnsavedActivity();
        }
      });
    });

    // hide tab bar on specific pages
    this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        takeUntil(this.closed$)
      )
      .subscribe((event: any) => {
        const url = event.urlAfterRedirects || event.url;
        console.log('Navigated to', redactUrl(url));

        if (url.indexOf('tracking') !== -1 || url.indexOf('fullscreen') !== -1) {
          const tabBar = document.getElementById('my-tabs');
          if (tabBar !== null && tabBar.style.display !== 'none') {
            tabBar.style.display = 'none';
          }
        } else {
          const tabBar = document.getElementById('my-tabs');
          if (tabBar !== null && tabBar.style.display === 'none') {
            tabBar.style.display = 'inherit';
          }
        }
      });

    try {
      if (this.platformService.isNative()) {
        console.log('Hiding splash screen');
        await SplashScreen.hide({ fadeOutDuration: 100 });
      }
    } catch (e) {
      console.error('Error hiding splash screen', e);
    }
    console.log('ngOnInit AppComponent done');
  }

  private async initializeApp(): Promise<void> {
    console.log('initializeApp');
    await this.platform.ready();
    await this.platformService.initialize();

    console.log('initialize logging and set platform and version', this.platform.platforms());
    try {
      const info = await this.app.getInfo();
      await this.loggingService.init(info.version);

      AppVersionInterceptor.setPlatformAndVersion(this.platform.platforms(), info.version);
    } catch (e) {
      await this.loggingService.init(environment.production ? 'web-prod' : 'web-dev');
      AppVersionInterceptor.setPlatformAndVersion(['browser'], environment.production ? 'prod' : 'dev');
    }

    await App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.ngZone.run(() => {
        console.log('appUrlOpen', JSON.stringify({ ...event, url: redactUrl(event.url) }));
        this.handleOpenURL(event.url);
      });
    });

    if (this.platformService.isNative()) {
      console.log('Set status bar style');
      await this.statusBarService.setStyle('LIGHT');
    }

    await this.initTranslate();

    // set the style of the status bar to default
    if (this.platform.is('android')) {
      //  await this.statusBarService.setOverlaysWebView({ overlay: true });
      // With Capacitor 8 and Android 15 edge-to-edge is default.
      // We ensure the status bar is transparent if needed, although setOverlaysWebView(true) usually does this.
    }

    await this.liveUpdateService.initialise();

    await this.audioService.configureAudio();

    // override the back button press
    const event = fromEvent(document, 'backbutton');

    this.backButtonSubscription = event.subscribe(async event => {
      const modal = await this.modalController.getTop();
      if (modal) {
        event.preventDefault();
        event.stopPropagation();
        await this.screenOrientation.unlock();
        await modal.dismiss();
      }
    });

    const launchUrl = await App.getLaunchUrl();
    let currentRoute = this.location.path(false);
    if ((!currentRoute || currentRoute.trim().length === 0) && !!launchUrl && !!launchUrl.url) {
      const url = new URL(launchUrl.url);
      currentRoute = url.pathname + url.search;
    }

    console.log(
      'initializeApp done, route',
      redactUrl(currentRoute),
      ...(launchUrl?.url ? ['launchUrl', redactUrl(launchUrl.url)] : [])
    );

    if (await this.authServerProvider.isAuthenticated()) {
      setTimeout(() => {
        this.activityStorageService.checkForUnsavedActivity();
        this.locationService.sendLogs().catch(e => console.error('Error sending location logs', e));
      }, 1000);

      this.store.dispatch(profileApiActions.loadProfile());
      if (currentRoute === '/') {
        console.log('navigating home...');
        await this.router.navigate([AppRoutes.TABS_HOME]);
      }
    } else {
      if (
        currentRoute.startsWith('/partner/') ||
        currentRoute.startsWith('/reset/') ||
        currentRoute.startsWith('/company/') ||
        currentRoute === AppRoutes.OAUTH2 ||
        currentRoute.startsWith(`${AppRoutes.OAUTH2}?`)
      ) {
        console.log('not authenticated. Keeping route as is ', redactUrl(currentRoute));
      } else {
        console.log('not authenticated. navigating welcome page');
        await this.router.navigate([AppRoutes.WELCOME]);
      }
    }

    await this.app.addListener('appStateChange', state => {
      if (state.isActive) {
        if (!this.stateService.getState()?.isTrackingActivity) {
          if (this.window.indexedDB) {
            this.window.indexedDB.databases().catch(error => {
              console.error('error getting databases when app resumed', error);
              //TODO check bugfender logs if error correlates with "UnknownError: Connection to Indexed Database server lost. Refresh the page to try again"
              //TODO if so, reload app
            });
          }
        }
      }
    });
  }

  private async registerBackgroundTask(): Promise<void> {
    if (this.platformService.isNative()) {
      const status = await BackgroundFetch.configure(
        {
          minimumFetchInterval: 30,
        },
        async taskId => {
          console.log('[BackgroundFetch] EVENT:', taskId);
          // Perform your work in an awaited Promise
          const result = await this.healthStatsService.doBackgroundFetch();
          console.log('[BackgroundFetch] work complete:', result);
          // [REQUIRED] Signal to the OS that your work is complete.
          BackgroundFetch.finish(taskId);
        },
        async taskId => {
          // The OS has signalled that your remaining background-time has expired.
          // You must immediately complete your work and signal #finish.
          console.log('[BackgroundFetch] TIMEOUT:', taskId);
          // [REQUIRED] Signal to the OS that your work is complete.
          BackgroundFetch.finish(taskId);
        }
      );

      // Checking BackgroundFetch status:
      if (status !== BackgroundFetch.STATUS_AVAILABLE) {
        // Uh-oh:  we have a problem:
        if (status === BackgroundFetch.STATUS_DENIED) {
          console.log('The user explicitly disabled background behavior for this app or for the whole system.');
        } else if (status === BackgroundFetch.STATUS_RESTRICTED) {
          console.log('Background updates are unavailable and the user cannot enable them again.');
        }
      }
    }
  }

  public async handleOpenURL(urlString: string): Promise<void> {
    console.log('handleOpenUrl', redactUrl(urlString));

    if (this.platform.is('ios')) {
      try {
        await this.browser.close();
      } catch (e) {
        // ignore
      }
    }

    const url = new URL(urlString);
    const queryParams = Object.fromEntries(url.searchParams.entries());
    let route: string;

    const urlScheme = await this.envService.urlScheme();

    if (urlString.startsWith(`${urlScheme}://`)) {
      const end = urlString.indexOf('?');
      route = urlString.slice(urlScheme.length + 2, end > 0 ? end : urlString.length);
    } else {
      route = url.pathname;
    }

    if (urlString.startsWith(`${urlScheme}://oauth2`) || route.startsWith(AppRoutes.OAUTH2)) {
      console.log('Navigating to oauth2', route, redactParams(queryParams));
      await this.navController.navigateForward(AppRoutes.OAUTH2, { queryParams });
    } else {
      if (await this.authServerProvider.isAuthenticated()) {
        this.store.dispatch(profileApiActions.loadProfile());

        if (await this.customerAppService.isCustomAppAndPromotionCodeMustBeEntered()) {
          console.log(
            'Not navigating to',
            route,
            redactParams(queryParams),
            url.hash.slice(1),
            'because promotion code must be entered first'
          );
          return;
        }
      }

      if (this.stateService.getState().isTrackingActivity) {
        console.warn("Ignoring deeplink because we're tracking an activity", redactUrl(urlString));
        return;
      } else {
        console.log('Navigating to', route, redactParams(queryParams), url.hash.slice(1));
        await this.navController.navigateForward(route, {
          queryParams,
          fragment: url.hash.slice(1),
        });
      }
    }
  }

  public async initTranslate(): Promise<void> {
    const enLang = 'en';
    // Set the default language for translation strings, and the current language.
    this.translate.setDefaultLang(enLang);

    const currentLanguage = await this.localStorageService.retrieve('locale');
    const browserLang = this.getBrowserLang();

    console.log('currentLanguage, browserLang', currentLanguage, browserLang);

    if (!currentLanguage) {
      console.log(
        `User's browser language: ${this.translateService.getBrowserLang()}; setting language to ${browserLang}`
      );
      await this.localStorageService.store('locale', browserLang);
    }
    this.translate.use(currentLanguage ?? browserLang);
    moment.locale(currentLanguage ?? browserLang);

    firstValueFrom(this.store.select(selectCustomer)).then(customer => {
      if (customer) {
        console.log('setting language from customer', customer.language);
        this.translate.use(customer.language);
        moment.locale(customer.language);
        this.localStorageService.store('locale', customer.language);
      }
    });
  }

  private getBrowserLang(): string {
    const lang = this.translateService.getBrowserLang();
    return lang === 'en' || lang === 'de' ? lang : 'en';
  }

  private setCustomClass(): void {
    this.router.events.pipe(takeUntil(this.closed$)).subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.customClass.set(
          event.url.includes('/team-hub') || event.url.includes('/admin-dashboard') ? '' : 'auto-width'
        );
      }
    });
  }

  public async ngOnDestroy(): Promise<void> {
    console.log('ngOnDestroy AppComponent');
    this.closed$.next(); // <-- close subscription when component is destroyed
    this.locationService.setDesiredState('stopped');
    await this.heartRateService.disconnect();
    this.backButtonSubscription.unsubscribe();
  }

  protected async goToCoachPage(): Promise<void> {
    await this.navController.navigateForward(AppRoutes.AI_COACH);
  }
}
